import React from 'react';
import { createPortal } from 'react-dom';
import Button from '../../../ui/Button';

export interface RepositoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Modal title, e.g. "Upload Image", "Create Note", "Upload PDF" */
  title: string;
  onSave: () => void;
  /** Save button label (default: "Save") */
  saveLabel?: string;
  /** Disable the save button */
  saveDisabled?: boolean;
  /** Title field value */
  itemTitle: string;
  onTitleChange: (v: string) => void;
  /** Optional tags field value (comma-separated) */
  tags?: string;
  onTagsChange?: (v: string) => void;
  /** Optional description field */
  description?: string;
  onDescriptionChange?: (v: string) => void;
  /** Widget-specific fields rendered before title (e.g. file input, preview) */
  topChildren?: React.ReactNode;
  /** Widget-specific fields rendered after title/description */
  children?: React.ReactNode;
  /** Hide title/tags/description fields (e.g. until a file is selected) */
  hideFields?: boolean;
}

/**
 * Shared modal for repository item create/edit operations.
 * Provides common fields (title, tags, description) and a children slot
 * for widget-specific content (file input, color picker, image preview, etc.).
 */
const RepositoryModal: React.FC<RepositoryModalProps> = ({
  isOpen,
  onClose,
  title,
  onSave,
  saveLabel = 'Save',
  saveDisabled = false,
  itemTitle,
  onTitleChange,
  tags,
  onTagsChange,
  description,
  onDescriptionChange,
  topChildren,
  children,
  hideFields,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black/70 transition-opacity" onClick={onClose} />
        <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col z-10">
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Widget-specific content before title (e.g. file input) */}
            {topChildren}

            {/* Title / Tags / Description — hidden until file is selected when hideFields is set */}
            {!hideFields && (
              <>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={itemTitle}
                    onChange={(e) => onTitleChange(e.target.value)}
                    placeholder="Enter title"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                  />
                </div>

                {onTagsChange && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Tags</label>
                    <input
                      type="text"
                      value={tags || ''}
                      onChange={(e) => onTagsChange(e.target.value)}
                      placeholder="tag1, tag2, tag3"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Separate tags with commas</p>
                  </div>
                )}

                {onDescriptionChange && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
                    <textarea
                      value={description || ''}
                      onChange={(e) => onDescriptionChange(e.target.value)}
                      placeholder="Optional notes"
                      rows={2}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm resize-none"
                    />
                  </div>
                )}
              </>
            )}

            {/* Widget-specific content */}
            {children}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onSave} disabled={saveDisabled}>
              {saveLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RepositoryModal;
